import React, { useState, useContext, useRef } from "react";
import { GlobalContext } from "../../../App";
import "./popupselect.css";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.pattern";

export default function BandChange() {
  const [show, setshow] = useState(false);
  const {
    vis,
    layerControls,
    map,
    selectedLayers,
    geoLayerName,
    geoWorkspace,
  } = useContext(GlobalContext);

  const toolvisRef = useRef(null);
  const [lLayer, setLLayer] = useState("");
  const [bands, setBands] = useState([]);
  const [redBand, setRedBand] = useState("");
  const [greenBand, setGreenBand] = useState("");
  const [blueBand, setBlueBand] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch band names from GeoServer WCS using the actual stored layer name
  const getBandNames = async (layerName, workspace) => {
    const url = `https://geoserver.vasundharaa.in/geoserver/${workspace}/wcs?service=WCS&version=1.1.1&request=DescribeCoverage&identifiers=${workspace}:${layerName}`;
    // console.log("WCS URL:", url);

    try {
      const res = await fetch(url);
      const xmlText = await res.text();
      // console.log("WCS XML response:", xmlText);

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "application/xml");
      const keys = Array.from(xmlDoc.getElementsByTagNameNS("*", "Key"));
      const bandList = keys.map((el) => el.textContent.trim()).filter(Boolean);

      // Fallback: if WCS returns no <Key> tags, generate generic band names
      // by reading the AxisDescription count
      if (bandList.length === 0) {
        const axes = Array.from(
          xmlDoc.getElementsByTagNameNS("*", "AxisDescription")
        );
        console.warn(
          "No <Key> tags found. Axes found:",
          axes.length,
          "— generating generic band names."
        );
        // Try to get band count from RangeSet/values
        const singleValues = Array.from(
          xmlDoc.getElementsByTagNameNS("*", "singleValue")
        );
        const count = singleValues.length || axes.length;
        if (count > 0) {
          return Array.from({ length: count }, (_, i) => `Band_${i + 1}`);
        }
        // Last resort: try GetCapabilities to count bands
        return await getBandCountFallback(layerName, workspace);
      }

      return bandList;
    } catch (err) {
      console.error("Error fetching band names:", err);
      return [];
    }
  };

  // Fallback: use WCS GetCoverage describe to count bands
  const getBandCountFallback = async (layerName, workspace) => {
    try {
      const url = `https://geoserver.vasundharaa.in/geoserver/${workspace}/wcs?service=WCS&version=2.0.1&request=DescribeCoverage&coverageId=${workspace}__${layerName}`;
      // console.log("Fallback WCS 2.0.1 URL:", url);
      const res = await fetch(url);
      const xmlText = await res.text();
      // console.log("Fallback XML:", xmlText);
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "application/xml");
      const fields = Array.from(
        xmlDoc.getElementsByTagNameNS("*", "field")
      );
      if (fields.length > 0) {
        return fields.map((f, i) => {
          const nameEl = f.getElementsByTagNameNS("*", "name")[0];
          return nameEl ? nameEl.textContent.trim() : `Band_${i + 1}`;
        });
      }
      return [];
    } catch (err) {
      console.error("Fallback band fetch failed:", err);
      return [];
    }
  };

  // When user selects a layer from the dropdown
  const handleChange = async (e) => {
    const label = e.target.value;
    setLLayer(label);
    setError("");
    setBands([]);
    setRedBand("");
    setGreenBand("");
    setBlueBand("");

    if (!label) return;

    // Use the actual GeoServer layer name stored in context after upload
    const layerName = geoLayerName;
    const workspace = geoWorkspace || "useruploads";

    if (!layerName) {
      setError(
        "No uploaded raster layer found. Please upload a raster image first."
      );
      return;
    }

    // console.log("Fetching bands for:", workspace, layerName);
    setLoading(true);
    const bandList = await getBandNames(layerName, workspace);
    setLoading(false);
    // console.log("Fetched bands:", bandList);

    if (bandList.length === 0) {
      setError(
        "Could not fetch band names. Make sure the layer is a multi-band raster."
      );
    }
    setBands(bandList);
  };

  const changeBandComposition = () => {
    const r = bands.indexOf(redBand) + 1;
    const g = bands.indexOf(greenBand) + 1;
    const b = bands.indexOf(blueBand) + 1;

    if (!r || !g || !b) {
      alert("Please select valid bands for R, G, and B.");
      return;
    }

    const workspace = geoWorkspace || "useruploads";
    const layerName = `${workspace}:${geoLayerName}`;
    const wmsBase = `https://geoserver.vasundharaa.in/geoserver/${workspace}/wms`;

    // console.log("Band composition for layer:", layerName);

    // Remove previous band composition layer
    if (map._styledBandLayer && map.hasLayer(map._styledBandLayer)) {
      map.removeLayer(map._styledBandLayer);
      if (layerControls) {
        layerControls.removeLayer(map._styledBandLayer);
      }
    }

    const sldBody = `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0"
  xmlns="http://www.opengis.net/sld"
  xmlns:ogc="http://www.opengis.net/ogc">
  <NamedLayer>
    <Name>${layerName}</Name>
    <UserStyle>
      <FeatureTypeStyle>
        <Rule>
          <RasterSymbolizer>
            <ChannelSelection>
              <RedChannel><SourceChannelName>${r}</SourceChannelName></RedChannel>
              <GreenChannel><SourceChannelName>${g}</SourceChannelName></GreenChannel>
              <BlueChannel><SourceChannelName>${b}</SourceChannelName></BlueChannel>
            </ChannelSelection>
            <ContrastEnhancement><Normalize/></ContrastEnhancement>
          </RasterSymbolizer>
        </Rule>
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`;

    // Custom tile layer with per-tile bbox injection
    const styledLayer = L.tileLayer(
      `${wmsBase}?service=WMS&version=1.1.0&request=GetMap&layers=${encodeURIComponent(
        layerName
      )}&format=image/png&transparent=true&width=256&height=256`,
      { tileSize: 256, transparent: true, opacity: 1 }
    );

    styledLayer.getTileUrl = function (coords) {
      const tileBounds = this._tileCoordsToBounds(coords);
      const sw = L.CRS.EPSG3857.project(tileBounds.getSouthWest());
      const ne = L.CRS.EPSG3857.project(tileBounds.getNorthEast());
      const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;

      const tileParams = new URLSearchParams({
        service: "WMS",
        version: "1.1.0",
        request: "GetMap",
        layers: layerName,
        format: "image/png",
        transparent: "true",
        srs: "EPSG:3857",
        width: "256",
        height: "256",
        bbox: bbox,
        SLD_BODY: sldBody,
      });

      return `${wmsBase}?${tileParams.toString()}`;
    };

    styledLayer.addTo(map).bringToFront();

    const label = `Band Composition: R${r} G${g} B${b}`;
    if (layerControls) {
      layerControls.addOverlay(styledLayer, label);
    }

    map._styledBandLayer = styledLayer;
    // console.log(`Band Composition Layer Added: ${label}`);
  };

 return (
  <div style={{ position: vis ? "absolute" : "relative" }} className="toolscont">

    {/* ── Trigger Button ── */}
    <button
      title="Band Composition"
      className="btn text-white"
      id="Symbology"
      onClick={() => setshow((prev) => !prev)}
      style={{
        zIndex: "1000", fontSize: "15px", backgroundColor: "black",
        padding: "2px", width: "40px", height: "40px",
        borderRadius: "50%", border: "none",
      }}
    >
      <i className="fa-solid fa-fill" />
    </button>

    {show && (
      <div
        ref={toolvisRef}
        className="toolsvis"
        id="toolvis"
        style={{ display: "flex", flexDirection: "column", width: "240px", padding: "10px 12px" }}
      >
        {/* ── Header ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingBottom: "8px", marginBottom: "10px",
          borderBottom: "1px solid rgba(255,255,255,0.12)"
        }}>
          <span style={{ color: "#ccc", fontSize: "13px", fontWeight: 600 }}>
            <i className="fa-solid fa-fill" style={{ marginRight: "6px", fontSize: "11px" }} />
            Band Composition
          </span>
          <span
            onClick={() => setshow(false)}
            style={{ color: "#e74c3c", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}
          >
            &times;
          </span>
        </div>

        {/* ── Content ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

          {/* Active Layer Status */}
          <p style={{
            fontSize: "11px", margin: 0, padding: "5px 8px", borderRadius: "4px",
            color: geoLayerName ? "#2ecc71" : "#e74c3c",
            backgroundColor: geoLayerName ? "rgba(46,204,113,0.1)" : "rgba(231,76,60,0.1)",
            border: `1px solid ${geoLayerName ? "rgba(46,204,113,0.3)" : "rgba(231,76,60,0.3)"}`,
          }}>
            {geoLayerName
              ? <><strong>Active:</strong> {geoLayerName}</>
              : "No raster layer uploaded yet."
            }
          </p>

          {/* Layer Select */}
          <div>
            <label style={{ fontSize: "11px", color: "#aaa", marginBottom: "3px", display: "block" }}>
              Select Layer
            </label>
            <select
              value={lLayer}
              className="form-select"
              onChange={handleChange}
              style={{ backgroundColor: "transparent", fontSize: "12px", color: "white", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "4px" }}
            >
              <option value="" style={{ backgroundColor: "black" }}>Select Layer</option>
              {Object.keys(selectedLayers)
                .filter((key) => !key.toLowerCase().includes("basemap"))
                .map((key) => (
                  <option key={key} value={key} style={{ backgroundColor: "black" }}>
                    {key.split("#")[0]}
                  </option>
                ))}
            </select>
          </div>

          {/* Loading / Error */}
          {loading && (
            <p style={{ fontSize: "11px", color: "#7fb3f5", margin: 0 }}>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Loading bands...
            </p>
          )}
          {error && (
            <p style={{ fontSize: "11px", color: "#e74c3c", margin: 0 }}>{error}</p>
          )}

          {/* R / G / B Band Selectors */}
          {[
            { label: "R", value: redBand,   fn: setRedBand },
            { label: "G", value: greenBand, fn: setGreenBand },
            { label: "B", value: blueBand,  fn: setBlueBand },
          ].map(({ label, value, fn }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "12px", fontWeight: 700, color: "white",
                width: "14px", flexShrink: 0, textAlign: "center"
              }}>
                {label}
              </span>
              <select
                className="form-select"
                value={value}
                onChange={(e) => fn(e.target.value)}
                style={{ flex: 1, backgroundColor: "transparent", fontSize: "12px", color: "white", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "4px" }}
              >
                <option style={{ backgroundColor: "black" }}>Select</option>
                {bands.map((b, i) => (
                  <option key={i} value={b} style={{ backgroundColor: "black" }}>{b}</option>
                ))}
              </select>
            </div>
          ))}

          {/* Apply Button */}
          <button
            className="btn btn-primary border-0"
            style={{ width: "100%", height: "30px", fontSize: "12px", marginTop: "2px" }}
            onClick={changeBandComposition}
            disabled={!redBand || !greenBand || !blueBand}
          >
            Change Bands
          </button>

        </div>
      </div>
    )}
  </div>
);
}